import type { EntityATI } from '@atlassian/search-client';

import {
	type TypeFilterConfig3P,
	TypeFilterValueKey,
} from '../../constants/filters/universal-type';

/**
 * Maps CloudGraph ATI to TypeFilterValueKey
 */
const ATI_TO_TYPE_FILTER_KEY_MAP: Record<string, TypeFilterValueKey> = {
	'ati:cloud:graph:document': TypeFilterValueKey.Document,
	'ati:cloud:graph:message': TypeFilterValueKey.Message,
	'ati:cloud:graph:conversation': TypeFilterValueKey.Conversation,
	'ati:cloud:graph:design': TypeFilterValueKey.Design,
	'ati:cloud:graph:video': TypeFilterValueKey.Video,
	'ati:cloud:graph:space': TypeFilterValueKey.Space,
	'ati:cloud:graph:comment': TypeFilterValueKey.Comment,
	'ati:cloud:graph:repository': TypeFilterValueKey.Repository,
	'ati:cloud:graph:pull-request': TypeFilterValueKey.PullRequest,
	'ati:cloud:graph:commit': TypeFilterValueKey.Commit,
	'ati:cloud:graph:branch': TypeFilterValueKey.Branch,
	'ati:cloud:graph:calendar-event': TypeFilterValueKey.Calendar,
	'ati:cloud:graph:work-item': TypeFilterValueKey.WorkItem,
	'ati:cloud:graph:project': TypeFilterValueKey.Project,
	'ati:cloud:graph:deal': TypeFilterValueKey.Deal,
	'ati:cloud:graph:customer-org': TypeFilterValueKey.CustomerOrg,
	'ati:cloud:graph:dashboard': TypeFilterValueKey.Dashboard,
	'ati:cloud:graph:data-table': TypeFilterValueKey.DataTable,
};

/**
 * Checks if an ATI string is a CloudGraph ATI
 */
const isCloudGraphAti = (ati: string): boolean => {
	return ati.startsWith('ati:cloud:graph:');
};

/**
 * Maps entity type strings from the API to TypeFilterConfig3P
 * The API now returns entityTypes directly in ATI format (e.g., "ati:cloud:graph:document")
 */
export function mapAtisToTypeFilterConfig(atis: string[]): TypeFilterConfig3P {
	const typeFilterConfig: TypeFilterConfig3P = new Map();

	// Group ATIs by their corresponding TypeFilterValueKey
	const atisByTypeKey = new Map<TypeFilterValueKey, EntityATI[]>();

	for (const ati of atis) {
		if (isCloudGraphAti(ati)) {
			const typeKey = ATI_TO_TYPE_FILTER_KEY_MAP[ati];

			if (typeKey) {
				if (!atisByTypeKey.has(typeKey)) {
					atisByTypeKey.set(typeKey, []);
				}

				atisByTypeKey.get(typeKey)!.push(ati as EntityATI);
			}
		}
	}

	// Convert to TypeFilterConfig3P format
	for (const [typeKey, entityAtis] of atisByTypeKey) {
		typeFilterConfig.set(typeKey, {
			entities3P: entityAtis,
			subTypes3P: [], // Most configs use empty subTypes3P, can be extended if needed
		});
	}

	return typeFilterConfig;
}
